import { useCallback } from 'react';

/**
 * Google Tag Manager event helper.
 *
 * GTM is wired in `gatsby-config.ts` via `gatsby-plugin-google-tagmanager` with a
 * renamed dataLayer (`dataLayerName: 'PageRouteChange'`) and only loads in production
 * (`GATSBY_ENV === 'prod'` + `GATSBY_GTM_ID`). Events must therefore be pushed onto
 * `window.PageRouteChange`, not the default `window.dataLayer`.
 *
 * In non-prod environments GTM is absent; pushes are harmless no-ops that simply
 * accumulate on the array until (if ever) a container reads it.
 */
const DATA_LAYER_NAME = 'PageRouteChange';

type GTMEventPayload = Record<string, unknown> & { event: string };

const pushToDataLayer = (payload: GTMEventPayload): void => {
    // SSR guard: `window` is undefined during Gatsby build.
    if (typeof window === 'undefined') return;

    const win = window as typeof window & { [DATA_LAYER_NAME]?: GTMEventPayload[] };
    win[DATA_LAYER_NAME] = win[DATA_LAYER_NAME] || [];
    win[DATA_LAYER_NAME].push(payload);
};

export const useGTM = () => {
    /**
     * Track a user click on an interactive element.
     *
     * Pushes the `user_interaction` event carrying `mdc_label` — the exact event name and
     * Data Layer Variable the GTM container listens for (the "Amplitude Event - CTA Clicked"
     * tag fires on `_event === 'user_interaction'` and reads `DLV - mdc_label`). `category`
     * and `label` are passed as extra context for any future tag/variable mappings.
     *
     * @param id        Stable, kebab-case identifier; becomes `mdc_label` (e.g. `analyze-web-scan-button`).
     * @param category  Feature area the element belongs to (e.g. `Analyze`, `Settings`).
     * @param label     Optional human-readable description of the action.
     */
    const trackClick = useCallback((id: string, category: string, label?: string): void => {
        pushToDataLayer({ event: 'user_interaction', mdc_label: id, category, label });
    }, []);

    /** Track an arbitrary custom event with an explicit name and payload. */
    const trackCustomEvent = useCallback((event: string, data: Record<string, unknown> = {}): void => {
        pushToDataLayer({ event, ...data });
    }, []);

    /**
     * Track a file submitted for scanning.
     *
     * Pushes the `file_upload` event with the variables the GTM container reads
     * (`file_type`, `file_size`, `success`).
     *
     * @param fileType  MIME type of the uploaded file (e.g. `application/pdf`).
     * @param fileSize  File size in bytes.
     * @param success   Whether the submission succeeded.
     */
    const trackFile = useCallback((fileType: string, fileSize: number, success: boolean): void => {
        pushToDataLayer({ event: 'file_upload', file_type: fileType, file_size: fileSize, success });
    }, []);

    /**
     * Track a form submission (e.g. a settings form being saved).
     *
     * Pushes the `form_submit` event carrying `form_name` — the GTM container's
     * `DLV - mdc_form_name` reads this and maps it to the Amplitude `feature_name`
     * for the "Feature Used (Form Submit)" tag. Extra fields are spread in as context.
     *
     * @param formName  Stable form identifier; becomes `form_name` (e.g. `settings-profile-save-button`).
     * @param formData  Optional extra context to attach to the event.
     */
    const trackForm = useCallback((formName: string, formData: Record<string, unknown> = {}): void => {
        pushToDataLayer({ event: 'form_submit', form_name: formName, ...formData });
    }, []);

    return { trackClick, trackCustomEvent, trackFile, trackForm };
};
